import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../store';
import { setCredentials } from '../store/slices/authSlice';
import { SessionManager } from '../utils/sessionManager';
import apiService from '../services/api';

const GoogleCallback: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get token from URL params
        const token = searchParams.get('token');
        const errorParam = searchParams.get('error');

        if (errorParam) {
          setError('Google authentication failed. Please try again.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        if (!token) {
          setError('No authentication token received.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Store token
        SessionManager.setToken(token);

        // Fetch user data
        const userResponse = await apiService.getCurrentUser();
        if (userResponse.success && userResponse.result) {
          SessionManager.setUser(userResponse.result);

          // Fetch permissions
          const permissionsResponse = await apiService.getUserPermissions();

          // Update Redux store
          dispatch(setCredentials({
            user: userResponse.result,
            token,
            permissions: permissionsResponse.result?.permissions || []
          }));

          // Redirect to dashboard
          navigate('/');
        } else {
          throw new Error('Failed to fetch user data');
        }
      } catch (err) {
        console.error('Google OAuth callback error:', err);
        setError('Authentication failed. Please try again.');
        SessionManager.clearSession();
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, dispatch]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 text-center">
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{error}</p>
            <p className="text-xs text-gray-500 mt-2">Redirecting to login...</p>
          </div>
        ) : (
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Completing Google sign-in...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleCallback;

